import { KeyboardArrowDown } from "@mui/icons-material";
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/material";
import cx from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import DatePicker from "components/DatePicker/DatePicker";
import DotElastic from "components/DotElastic";
import {
  getAutopilotUsage,
  getAutopilotExportActivity,
} from "services/AutopilotService";
import {
  formatDateToYMD,
  formatMonthYear,
  getCurrencySymbol,
} from "utils/helper";

import styles from "./AutopilotUsageTab.module.scss";

interface MonthlyUsageStats {
  from_date: string;
  to_date: string;
  frames_used: number;
  assets_uploaded: number;
  inferences_ran: number;
  estimated_cost: null | {
    amount: number;
    currency: string;
  };
}

interface AutopilotUsage {
  [key: string]: MonthlyUsageStats;
}

enum exportDurations {
  _30D = "_30D",
  _90D = "_90D",
  _6M = "_6M",
  _1Y = "_1Y",
  CUSTOM = "CUSTOM",
}

const AutopilotUsageTab: React.FC = () => {
  const [usage, setUsage] = useState<AutopilotUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportDuration, setExportDuration] = useState<exportDurations | null>(
    null,
  );
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const getDurationLabel = (duration: exportDurations | null) => {
    if (!duration) return "Activity Period";

    switch (duration) {
      case exportDurations._30D:
        return "Last 30 days";
      case exportDurations._90D:
        return "Last 90 days";
      case exportDurations._6M:
        return "Last 6 months";
      case exportDurations._1Y:
        return "Last 1 year";
      case exportDurations.CUSTOM:
        return "Custom Period";
      default:
        return "Activity Period";
    }
  };

  const formatMonth = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "long",
      });
    } catch {
      return "current month";
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showDropdown && !(event.target as Element)?.closest(".dropdown")) {
        setShowDropdown(false);
        setExportDuration(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleExportActivity = useCallback(async () => {
    try {
      setExporting(true);

      if (!exportDuration) {
        toast.error("Please select a duration period");
        return;
      }

      let startDate: Date;
      let endDate: Date;

      if (exportDuration === exportDurations.CUSTOM) {
        if (!customStartDate || !customEndDate) {
          toast.error("Please select both start and end dates");
          return;
        }
        startDate = customStartDate;
        endDate = customEndDate;
      } else {
        // Calculate date range based on selected duration
        endDate = new Date();
        startDate = new Date();

        switch (exportDuration) {
          case exportDurations._30D:
            startDate.setDate(endDate.getDate() - 30);
            break;
          case exportDurations._90D:
            startDate.setDate(endDate.getDate() - 90);
            break;
          case exportDurations._6M:
            startDate.setMonth(endDate.getMonth() - 6);
            break;
          case exportDurations._1Y:
            startDate.setFullYear(endDate.getFullYear() - 1);
            break;
          // no default
        }
      }

      const response = await getAutopilotExportActivity(
        formatDateToYMD(startDate),
        formatDateToYMD(endDate),
      );
      const email = response?.email || "your registered email";
      toast.success(`Activity report will be sent to ${email} shortly`);
      setExportDuration(null);
      setShowDropdown(false);
    } catch (e) {
      toast.error("Failed to export activity report");
      console.error("Error exporting activity:", e);
    } finally {
      setExporting(false);
    }
  }, [exportDuration, customStartDate, customEndDate]);

  const handleDateChange = (startDate: Date | null, endDate: Date | null) => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
  };

  const handleApplyDates = async (
    startDate: Date | null,
    endDate: Date | null,
  ) => {
    if (startDate && endDate) {
      setShowDatePicker(false);
    }
  };

  const handleCancelDates = () => {
    setCustomStartDate(null);
    setCustomEndDate(null);
    setExportDuration(null);
    setShowDatePicker(false);
  };

  const handleCustomOptionChange = () => {
    setExportDuration(exportDurations.CUSTOM);
    setShowDatePicker(true);
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        setLoading(true);
        const data = await getAutopilotUsage();
        setUsage(data);
      } catch (err) {
        setError("Failed to load autopilot usage data");
        console.error("Error fetching autopilot usage:", err);
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchUsage();
  }, []);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <DotElastic />
        </div>
      </div>
    );
  }

  if (error) {
    return null;
  }

  const sortedDates = usage
    ? Object.keys(usage).sort(
        (a, b) => new Date(b).getTime() - new Date(a).getTime(),
      )
    : [];
  const latestUsage =
    sortedDates.length > 0 && usage ? usage[sortedDates[0]] : null;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.tabs}>
          <div className={`${styles.tab} ${styles.tabSelected}`}>Usage</div>
        </div>
        <div className={styles.exportContainer}>
          {usage && !!Object.keys(usage).length && (
            <div style={{ position: "relative" }}>
              <Button
                className={cx("outlineButton", styles.exportBtn)}
                onClick={() => setShowDropdown(!showDropdown)}
              >
                {getDurationLabel(exportDuration)} <KeyboardArrowDown />
              </Button>

              {showDropdown && (
                <div className={`${styles.dropdown} dropdown`}>
                  <div className={styles.dropdownContent}>
                    <RadioGroup
                      value={exportDuration || ""}
                      onChange={(e) => {
                        const newDuration = e.target.value as exportDurations;
                        setExportDuration(newDuration);
                        if (newDuration === exportDurations.CUSTOM) {
                          setShowDatePicker(true);
                        } else {
                          setShowDatePicker(false);
                        }
                      }}
                      sx={{ color: "#CCCCDC" }}
                    >
                      <FormControlLabel
                        value={exportDurations._30D}
                        control={
                          <Radio className={styles.radioBtn} size="small" />
                        }
                        label="Last 30 days"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                            fontFamily: "Inter",
                          },
                        }}
                      />
                      <FormControlLabel
                        value={exportDurations._90D}
                        control={
                          <Radio className={styles.radioBtn} size="small" />
                        }
                        label="Last 90 days"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                            fontFamily: "Inter",
                          },
                        }}
                      />
                      <FormControlLabel
                        value={exportDurations._6M}
                        control={
                          <Radio className={styles.radioBtn} size="small" />
                        }
                        label="Last 6 months"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                            fontFamily: "Inter",
                          },
                        }}
                      />
                      <FormControlLabel
                        value={exportDurations._1Y}
                        control={
                          <Radio className={styles.radioBtn} size="small" />
                        }
                        label="Last 1 year"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                            fontFamily: "Inter",
                          },
                        }}
                      />
                      <FormControlLabel
                        value={exportDurations.CUSTOM}
                        control={
                          <Radio className={styles.radioBtn} size="small" />
                        }
                        label="Custom"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                            fontFamily: "Inter",
                          },
                        }}
                      />
                    </RadioGroup>

                    {exportDuration === exportDurations.CUSTOM &&
                      showDatePicker && (
                        <div className={styles.datePickerContainer}>
                          <DatePicker
                            startDate={customStartDate}
                            endDate={customEndDate}
                            onDateChange={handleDateChange}
                            onApply={handleApplyDates}
                            onCancel={handleCancelDates}
                            isVisible
                          />
                        </div>
                      )}

                    <div className={styles.dropdownFooter}>
                      <Button
                        className={cx("primaryButton", styles.getReportBtn)}
                        onClick={() => {
                          handleExportActivity();
                        }}
                        disabled={exporting || !exportDuration}
                      >
                        Get Report
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.boxes}>
        {!!latestUsage?.estimated_cost && (
          <div className={styles.box}>
            <div className={styles.boxHeader}>
              Estimated cost in{" "}
              {latestUsage?.to_date
                ? formatMonth(latestUsage.to_date)
                : "current month"}
            </div>
            <div>
              <span className={styles.boxPrimaryValue}>
                {getCurrencySymbol(latestUsage.estimated_cost.currency)}
                {latestUsage.estimated_cost.amount.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        <div className={styles.box}>
          <div className={styles.boxHeader}>
            Frames processed in{" "}
            {latestUsage?.to_date
              ? formatMonth(latestUsage.to_date)
              : "current month"}
          </div>
          <div>
            <span className={styles.boxPrimaryValue}>
              {latestUsage?.frames_used || 0}
            </span>
          </div>
        </div>

        <div className={styles.box}>
          <div className={styles.boxHeader}>
            Assets processed in{" "}
            {latestUsage?.to_date
              ? formatMonth(latestUsage.to_date)
              : "current month"}
          </div>
          <div>
            <span className={styles.boxPrimaryValue}>
              {latestUsage?.assets_uploaded || 0}
            </span>
          </div>
        </div>

        <div className={styles.box}>
          <div className={styles.boxHeader}>
            Inferences executed in{" "}
            {latestUsage?.to_date
              ? formatMonth(latestUsage.to_date)
              : "current month"}
          </div>
          <div>
            <span className={styles.boxPrimaryValue}>
              {latestUsage?.inferences_ran || 0}
            </span>
          </div>
        </div>
      </div>

      {usage && !!Object.keys(usage).length && (
        <div className={styles.usageCon}>
          <div className={styles.usageTableCon}>
            <table border={1} className={styles.usageTable}>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Frames Processed</th>
                  <th>Assets Processed</th>
                  <th>Inferences Executed</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(usage)
                  .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
                  .filter((dateKey) => {
                    const sixMonthsAgo = new Date();
                    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                    return (
                      new Date(dateKey).getTime() >= sixMonthsAgo.getTime()
                    );
                  })
                  .map((dateKey) => {
                    const monthlyStats = usage[dateKey];
                    if (!monthlyStats) return null;

                    return (
                      <tr key={dateKey}>
                        <td>{formatMonthYear(monthlyStats.to_date)}</td>
                        <td>{monthlyStats.frames_used}</td>
                        <td>{monthlyStats.assets_uploaded}</td>
                        <td>{monthlyStats.inferences_ran}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(!usage || Object.keys(usage).length === 0) && (
        <div className={styles.usageCon}>
          <div>No autopilot usage data available.</div>
        </div>
      )}
    </div>
  );
};

export default AutopilotUsageTab;
